import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../i18n";
import Text from "@ui/Text";
import { useDelete } from "../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import { useLocation, useNavigate } from "react-router-dom";
import type { ManualFaultEvent } from "../../../../../api/useManualFaultEvents";
import { matchRouterPath, toPath } from "../../../../../router/helpers/path";

interface P {
  projectId: string;
  manualFaultEvent: ManualFaultEvent;
  onRequestClose: () => void;
}

const ModalFormRemoveManualFaultEvent = ({ projectId, manualFaultEvent, onRequestClose }: P) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToasts();
  const deleteManualFaultEvent = useDelete(
    "/projects/:projectId(uuid)/manualFaultEvent/:manualFaultEventId(uuid)",
  );

  const redirection = React.useCallback(() => {
    let targetPath = toPath("/projects/:projectId/fault-events", {
      projectId,
    });
    const previousLocationWasClosedFaultEventsPage =
      location.state === null
        ? false
        : matchRouterPath("/projects/:projectId/fault-events/closed", location.state.from);
    if (previousLocationWasClosedFaultEventsPage) {
      targetPath = toPath("/projects/:projectId/fault-events/closed", {
        projectId,
      });
    }

    void navigate(targetPath);
  }, [projectId, location, navigate]);

  const handleSubmit = React.useCallback(async () => {
    try {
      await deleteManualFaultEvent({
        routeParams: { projectId, manualFaultEventId: manualFaultEvent.id },
      });
      addToast(
        "success",
        t("project.manual_fault_event.header.modal_form.remove.success", manualFaultEvent.name),
      );
    } catch (e) {
      addToast("error", t("errors.backend_error"));
    }
    redirection();
    return true;
  }, [deleteManualFaultEvent, projectId, manualFaultEvent, addToast, redirection]);

  return (
    <ModalForm
      onSubmit={handleSubmit}
      onRequestClose={onRequestClose}
      okButtonName={t("modalForm.button.name.delete")}
      okButtonVariant="destructive"
    >
      <Text>
        {t(
          "project.manual_fault_event.modal_form.remove.text",
          <Text type="body1SemiBold" as="span">
            {manualFaultEvent.name}
          </Text>,
        )}
      </Text>
    </ModalForm>
  );
};

export default React.memo(ModalFormRemoveManualFaultEvent);
